import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | forgot_sent
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.toLowerCase().trim(), password);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales no válidas");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.toLowerCase().trim() });
      setMode("forgot_sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo procesar la solicitud");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBFAF6]">
      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-10">
        {mode === "login" && (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
            <Logo size={40} />
            <div>
              <h1 className="font-heading text-4xl font-bold text-[#2C2C2A] tracking-tight">Inicia sesión</h1>
              <p className="text-sm text-[#7A786F] mt-1">Accede para gestionar tus videoperitaciones.</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required
                  placeholder="perito@aseguradora.es"
                  data-testid="login-email-input"
                  className="rounded-[12px] border-[#E5E2D8]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="pwd">Contraseña</Label>
                  <button
                    type="button"
                    onClick={() => { setForgotEmail(email); setMode("forgot"); }}
                    className="text-xs text-[#D85A30] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    ¿La has olvidado?
                  </button>
                </div>
                <Input
                  id="pwd" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  data-testid="login-password-input"
                  className="rounded-[12px] border-[#E5E2D8]"
                />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-eks-primary rounded-[12px] h-11" data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Entrar
            </Button>
            <p className="text-xs text-[#7A786F] text-center">
              Conexión cifrada · RGPD · Sector asegurador
            </p>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={submitForgot} className="w-full max-w-sm space-y-6" data-testid="forgot-form">
            <Logo size={40} />
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#2C2C2A] tracking-tight">Restablece tu contraseña</h1>
              <p className="text-sm text-[#7A786F] mt-1">Indícanos tu email y te enviaremos un enlace para crear una nueva.</p>
            </div>
            <div>
              <Label htmlFor="forgot-email">Correo electrónico</Label>
              <Input
                id="forgot-email" type="email" required
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="tu@correo.es"
                className="rounded-[12px] border-[#E5E2D8]"
                data-testid="forgot-email-input"
              />
            </div>
            <Button type="submit" disabled={forgotLoading} className="w-full btn-eks-primary rounded-[12px] h-11" data-testid="forgot-submit-btn">
              {forgotLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Enviarme el enlace
            </Button>
            <button
              type="button"
              onClick={() => setMode("login")}
              className="w-full text-xs text-[#7A786F] hover:text-[#2C2C2A] inline-flex items-center justify-center"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="h-3 w-3 mr-1" /> Volver al inicio de sesión
            </button>
          </form>
        )}

        {mode === "forgot_sent" && (
          <div className="w-full max-w-sm space-y-5 text-center" data-testid="forgot-sent">
            <Logo size={40} />
            <Mail className="h-12 w-12 text-[#1D9E75] mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">Revisa tu correo</h1>
            <p className="text-sm text-[#7A786F]">
              Si existe una cuenta con <b className="text-[#2C2C2A]">{forgotEmail}</b>, hemos enviado un enlace para restablecer tu contraseña.
              Caduca en 60 minutos.
            </p>
            <Button onClick={() => setMode("login")} variant="outline" className="rounded-[12px] border-[#E5E2D8]" data-testid="back-to-login-btn">
              <ArrowLeft className="h-3 w-3 mr-1" /> Volver al inicio de sesión
            </Button>
          </div>
        )}
      </div>
      {/* Brand side */}
      <div className="hidden lg:flex relative bg-[#1A1A1C] overflow-hidden">
        <div className="absolute inset-0 vp-grid-pattern opacity-20" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#D85A30] opacity-10 blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative z-10 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60">
            <span className="vp-live-dot" /> Videoperitación remota
          </div>
          <div className="space-y-6 max-w-md">
            <h2 className="font-heading text-5xl font-bold tracking-tight leading-[1.05]">
              Peritar a distancia, <span className="text-[#D85A30]">en directo y certificado.</span>
            </h2>
            <p className="text-white/70 text-base leading-relaxed">
              Videollamada con el asegurado sin instalar nada. Capturas geolocalizadas, transcripción
              automática en español y certificado PDF integrado con Ekspertium.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 text-xs uppercase tracking-wider text-white/55">
            <div><div className="text-2xl font-heading font-bold text-white">WebRTC</div>Videollamada</div>
            <div><div className="text-2xl font-heading font-bold text-white">Whisper</div>Transcripción ES</div>
            <div><div className="text-2xl font-heading font-bold text-white">RGPD</div>Consentimiento</div>
          </div>
        </div>
      </div>
    </div>
  );
}
